import { Button, PasswordInput, TextInput } from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { FC, useState } from 'react';
import { z } from 'zod';

import { Page } from '@/components/Page.tsx';

import { createMagicLink } from '@/services/authService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

const demo_login_form_schema = z.object({
    email: z.email('Ungültige E-Mail-Adresse'),
    password: z.string(),
});

type DemoLoginFormValues = z.infer<typeof demo_login_form_schema>;

const DemoLoginPage: FC = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [show_success, setShowSuccess] = useState<boolean>(false);

    const form = useForm<DemoLoginFormValues>({
        mode: 'controlled',
        initialValues: {
            email: '',
            password: '',
        },
        validateInputOnChange: true,
        validateInputOnBlur: true,
        validate: schemaResolver(demo_login_form_schema, { sync: true }),
    });

    const handleSubmit = async (values: DemoLoginFormValues): Promise<void> => {
        setLoading(true);
        const is_success: boolean = await createMagicLink(values.email, {
            is_demo_login: true,
            demo_password: values.password,
        });

        if (is_success) {
            setShowSuccess(true);
        } else {
            showErrorNotification('Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.');
        }
        setLoading(false);
    };

    if (show_success) {
        return (
            <Page className="items-center justify-center gap-8" fullScreen>
                <h1 className="text-4xl text-black">Demo CareFlow Login</h1>
                <div className="flex flex-col items-center gap-5">
                    <p className="text-md text-black">Vielen Dank!</p>
                    <p className="text-md text-black">Wir haben Ihnen einen Magic-Link per E-Mail gesendet.</p>
                    <p className="text-md text-black">Klicken Sie auf den Link in der E-Mail, um fortzufahren.</p>
                </div>
            </Page>
        );
    }

    return (
        <Page className="items-center justify-center gap-8" fullScreen>
            <h1 className="text-4xl text-black">Demo CareFlow Login</h1>
            <form onSubmit={form.onSubmit(handleSubmit)} className="flex flex-col items-center gap-4">
                <TextInput
                    className="w-60"
                    placeholder="test@test.com"
                    type="email"
                    label="E-Mail"
                    withAsterisk
                    disabled={loading}
                    {...form.getInputProps('email')}
                />
                <PasswordInput
                    className="w-60"
                    placeholder="Password"
                    label="Passwort"
                    withAsterisk
                    disabled={loading}
                    {...form.getInputProps('password')}
                />
                <Button className="w-60" type="submit" disabled={loading || !form.isValid()} loading={loading}>
                    Anmelden
                </Button>
            </form>
        </Page>
    );
};

export default DemoLoginPage;
